import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Box, Collapse, LinearProgress, SxProps, Toolbar } from '@mui/material';

import { getIsLoading } from '../../../store/LoaderSlice';
import { useAppSelector } from '../../../hooks';
import { SafeAreaGaps } from '../../../helper';
import InvalidRoute from '../../Validate/InvalidRoute';
import Header from './components/Header';
import Courses from '../Courses';
import ViewCourse from '../ViewCourse';
import CreateNewCoursePage from '../Courses/admin/CreateNewCoursePage';
import CourseSubscriptionProcessing from '../../Validate/CourseSubscriptionProcessing';
import Footer from './components/Footer';
import CheckPermission from './components/CheckPermission';
import Schedule from '../Schedule';
import Profile from '../Profile';
import RescheduleRequests from '../RescheduleRequests';
import LessonHistory from '../LessonHistory';
import Settings from '../Settings';
import TrialClassPage from '../TrialClass';
import Users from '../Users';
import ViewUser from '../ViewUser';
import Billing from '../Billing';
import BillingProcessingPage from '../Billing/BillingProcessingPage';
import PayInvoicePage from '../Billing/invoice/PayInvoicePage';
import InvoiceProcessingPage from '../Billing/invoice/InvoiceProcessingPage';
import { USER_PERMISSION_INDIVIDUALS } from '../../../store/V2/RolesSlice';
import TutorProfile from '../Tutor';
import Tutors from '../Tutors';

const loaderStyles: SxProps = {
  position: 'fixed',
  zIndex: 1,
  width: '100%',
  top: {
    xs: `calc(${SafeAreaGaps.top} + 59px)`,
    sm: `calc(${SafeAreaGaps.top} + 64px)`,
  },
};

const mainContainerStyles: SxProps = {
  flexGrow: 1,
  width: { sm: `100%` },
  // device height - (header height + footer top bg container height)
  minHeight: { xs: `calc(100vh - 154px)`, sm: `calc(100vh - 250px)` },
};

const getLayoutBackgroundColor = (pathName: string) => {
  let backgroundColor: string | undefined;

  switch (pathName) {
    case '/learning-portal/portal/billing':
      backgroundColor = '#E5F7F666';
      break;

    case '/learning-portal/portal/tutors':
      backgroundColor =
        'linear-gradient(to right, #FFFFFF -2.82%, #DAF4F3 100.08%)';
      break;

    default:
      backgroundColor = undefined;
      break;
  }

  return backgroundColor;
};

const getLayoutFooterGap = (pathName: string) => {
  let footerGap: string | undefined;

  switch (pathName) {
    case '/learning-portal/portal/billing':
      footerGap = '130px';
      break;

    default:
      footerGap = undefined;
      break;
  }

  return footerGap;
};

const PortalPage: React.FC = () => {
  // Store
  const isLoading = useAppSelector(getIsLoading);

  const layoutBackgroundColor = getLayoutBackgroundColor(
    window.location.pathname
  );

  const layoutFooterGap = getLayoutFooterGap(window.location.pathname);

  return (
    <>
      {/* Header */}
      <Header />
      <Toolbar />
      {/* Header Loader */}
      <Collapse in={isLoading} sx={loaderStyles}>
        <LinearProgress color="info" />
      </Collapse>
      {/* Routes */}
      <Box
        component="main"
        sx={{ ...mainContainerStyles, backgroundColor: layoutBackgroundColor }}
      >
        <Routes>
          <Route
            path="/"
            element={
              <Navigate to={`${process.env.PUBLIC_URL}/portal/courses`} />
            }
          />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:id" element={<ViewCourse />} />
          <Route
            path="/courses/new"
            element={
              <CheckPermission
                permission={{
                  adminCourse: [
                    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_CREATE,
                  ],
                }}
              >
                <CreateNewCoursePage />
              </CheckPermission>
            }
          />
          <Route
            path="/courses/subscription/processing/:stripeSessionId"
            element={<CourseSubscriptionProcessing />}
          />
          <Route
            path="/schedule"
            element={
              <CheckPermission
                permission={{
                  schedule: [USER_PERMISSION_INDIVIDUALS.SCHEDULE_VIEW],
                  adminSchedule: [
                    USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_VIEW,
                  ],
                }}
              >
                <Schedule />
              </CheckPermission>
            }
          />
          <Route
            path="/reschedule-requests"
            element={
              <CheckPermission
                permission={{
                  adminSchedule: [
                    USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_VIEW,
                  ],
                }}
              >
                <RescheduleRequests />
              </CheckPermission>
            }
          />
          <Route path="/profile" element={<Profile />} />
          <Route
            path="/lesson-history"
            element={
              <CheckPermission
                permission={{
                  adminLessonHistory: [
                    USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_VIEW,
                  ],
                  lessonHistory: [
                    USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_VIEW,
                  ],
                }}
              >
                <LessonHistory />
              </CheckPermission>
            }
          />
          <Route
            path="/settings"
            element={
              <CheckPermission
                permission={{
                  adminSettings: [
                    USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_VIEW,
                  ],
                }}
              >
                <Settings />
              </CheckPermission>
            }
          />
          <Route
            path="/billing"
            element={
              <CheckPermission
                permission={{
                  billing: [USER_PERMISSION_INDIVIDUALS.BILLING_VIEW],
                }}
              >
                <Billing />
              </CheckPermission>
            }
          />
          <Route
            path="/billing/processing/:stripeSessionId"
            element={<BillingProcessingPage />}
          />
          <Route
            path="/billing/invoice/pay/:invoiceId"
            element={<PayInvoicePage />}
          />
          <Route
            path="/billing/invoice/processing/:stripeSessionId"
            element={<InvoiceProcessingPage />}
          />
          <Route
            path="/users"
            element={
              <CheckPermission
                permission={{
                  adminUsers: [USER_PERMISSION_INDIVIDUALS.ADMIN_USERS_VIEW],
                }}
              >
                <Users />
              </CheckPermission>
            }
          />
          <Route
            path="/users/:id"
            element={
              <CheckPermission
                permission={{
                  adminUsers: [USER_PERMISSION_INDIVIDUALS.ADMIN_USERS_EDIT],
                }}
              >
                <ViewUser />
              </CheckPermission>
            }
          />
          <Route path="/trial-classes" element={<TrialClassPage />} />
          <Route path="/tutors" element={<Tutors />} />
          <Route path="/profile/:id" element={<TutorProfile />} />
          {/* Validate */}
          <Route path="*" element={<InvalidRoute />} />
        </Routes>
      </Box>
      <div style={{ height: '15px', background: layoutBackgroundColor }} />
      <Box
        sx={{
          background: `url('${
            process.env.PUBLIC_URL
          }/images/shapes/wave-with-gadgets.png') bottom left/contain repeat-x${
            layoutBackgroundColor ? `, ${layoutBackgroundColor}` : ''
          }`,
          height: { xs: '100px', sm: '186px' },
        }}
      />
      <Footer bottomGap={layoutFooterGap} />
    </>
  );
};

export default PortalPage;
